import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/hooks/useToast';

// Get API URL - use environment variable or default to relative path
const getApiBaseUrl = () => {
  return import.meta.env.VITE_API_URL || '/api';
};

// User-friendly error messages
const ERROR_MESSAGES: Record<number, { title: string; description: string }> = {
  403: {
    title: 'Access Denied',
    description: 'You do not have access to this resource. Please contact your administrator.',
  },
  404: {
    title: 'Not Found',
    description: 'The requested resource was not found.',
  },
  429: {
    title: 'Too Many Requests',
    description: 'Please wait a moment and try again.',
  },
  500: {
    title: 'Server Error',
    description: 'Something went wrong. Please try again later.',
  },
  502: {
    title: 'Server Unavailable',
    description: 'Server is temporarily unavailable. Please try again later.',
  },
  503: {
    title: 'Service Unavailable',
    description: 'Service is temporarily unavailable. Please try again later.',
  },
};

// Create axios instance
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to read a cookie value by name
function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : undefined;
}

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach CSRF token for state-changing requests
    const csrfToken = getCookie('csrfToken');
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ error?: string; errors?: Record<string, string[]> }>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { 
      _retry?: boolean;
      _skipErrorToast?: boolean;
    };
    const status = error.response?.status;

    // Handle 401 Unauthorized - try to refresh token
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = useAuthStore.getState().refreshToken;
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${getApiBaseUrl()}/auth/refresh`, {
          refreshToken,
        }, {
          withCredentials: true,
        });

        const { accessToken, refreshToken: newRefreshToken, permissions } = response.data;

        // Update tokens and permissions in store
        useAuthStore.getState().setTokens(accessToken, newRefreshToken);
        if (permissions) {
          useAuthStore.getState().setPermissions(permissions);
        }

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch {
        // Refresh failed - silently clear auth state
        useAuthStore.setState({
          user: null,
          accessToken: null,
          refreshToken: null,
          permissions: [],
          isAuthenticated: false,
          requires2FA: false,
          pendingUserId: null,
        });
        // Don't show toast or redirect — let the page handle unauthenticated state
        return Promise.reject(error);
      }
    }

    // Show toast for specific error codes (skip for validation errors - handled by forms)
    if (status && status !== 400 && status !== 422 && !originalRequest._skipErrorToast) {
      const errorConfig = ERROR_MESSAGES[status];
      
      if (errorConfig) {
        // Use server message if available, otherwise use default
        const serverMessage = error.response?.data?.error;
        toast({
          title: errorConfig.title,
          description: serverMessage || errorConfig.description,
          variant: 'destructive',
        });
      } else if (status >= 500) {
        // Generic server error
        toast({
          title: 'Server Error',
          description: 'Something went wrong. Please try again later.',
          variant: 'destructive',
        });
      }
    }

    // Handle network errors
    if (error.code === 'ERR_NETWORK') {
      toast({
        title: 'Connection Error',
        description: 'Unable to connect to server. Please check your internet connection.',
        variant: 'destructive',
      });
    }

    // Handle timeout
    if (error.code === 'ECONNABORTED') {
      toast({
        title: 'Request Timeout',
        description: 'The request took too long. Please try again.',
        variant: 'destructive',
      });
    }

    return Promise.reject(error);
  }
);

export default api;

/**
 * Create a request config that skips automatic error toast
 * Use this when you want to handle errors manually in the component
 */
export const skipErrorToast = () => ({
  _skipErrorToast: true,
});

// API Response Types
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}
